// src/domains/core/context-intelligence/registry/ruleRegistry.ts

import type { Rule } from "../types/rules.types";

/**
 * Rule Registry
 * Central registry for all declarative rules
 */
class RuleRegistry {
  private rules = new Map<string, Rule>();

  /**
   * Register a new rule
   *
   * @param rule - Rule to register
   *
   * @returns void
   *
   */
  register(rule: Rule): void {
    this.rules.set(rule.id, rule);
  }

  /**
   * Get a rule by ID
   *
   * @param id - Rule ID
   *
   * @returns Rule or undefined if not found
   *
   */
  get(id: string): Rule | undefined {
    return this.rules.get(id);
  }

  /**
   * Get all registered rules
   *
   * @returns Array of all registered rules
   *
   */
  getAll(): Rule[] {
    return [...this.rules.values()];
  }

  /**
   * Get enabled rules only
   * Automatically initializes registry if not already initialized
   *
   * @returns Array of enabled rules
   *
   */
  getEnabled(): Rule[] {
    // Lazy initialization - ensure registry is initialized
    if (this.rules.size === 0) {
      // Synchronous initialization - call directly
      initializeRuleRegistry();
    }
    return this.getAll().filter((rule) => rule.enabled);
  }

  /**
   * Get rules sorted by priority (highest first)
   *
   * @returns Array of rules sorted by priority
   *
   */
  getByPriority(): Rule[] {
    return this.getEnabled().toSorted((a: Rule, b: Rule): number => {
      return b.priority - a.priority;
    });
  }

  /**
   * Check if a rule is registered
   *
   * @param id - Rule ID
   *
   * @returns true if rule is registered, false otherwise
   *
   */
  has(id: string): boolean {
    return this.rules.has(id);
  }

  /**
   * Update rule execution metadata
   *
   * @param id
   *
   * @param success
   *
   */
  updateExecution(id: string, success: boolean): void {
    const rule = this.rules.get(id);
    if (rule) {
      rule.lastExecuted = Date.now();
      rule.executionCount = (rule.executionCount ?? 0) + 1;
      if (success) {
        rule.successCount = (rule.successCount ?? 0) + 1;
      }
    }
  }
}

/**
 * Singleton Rule Registry Instance
 */
export const ruleRegistry = new RuleRegistry();

/**
 * Initialize default rules for PoC
 * These rules will be expanded in Phase 5
 */
export function initializeRuleRegistry(): void {
  // Rule 1: Low Readiness (simplified for PoC - works even without tasks)
  ruleRegistry.register({
    action: {
      insightTemplate: {
        category: "health",
        description:
          "Your readiness score is below optimal ({{readiness}}). Consider taking it easy today and focusing on recovery.",
        priority: "high",
        recommendations: [
          "Review and prioritize your open tasks",
          "Consider deferring non-critical tasks",
          "Take breaks to improve recovery",
        ],
        title: "Low Readiness Score Detected",
      },
      type: "generateInsight",
    },
    condition: {
      operands: [
        {
          aggregation: "latest",
          indicatorId: "oura_readiness",
          operator: "<",
          threshold: 70,
        },
      ],
      operator: "AND",
    },
    description: "Detects when readiness score is below optimal",
    enabled: true,
    id: "rule-001",
    metadata: {
      createdAt: Date.now(),
      createdBy: "system",
      tags: ["health", "productivity", "overload"],
    },
    name: "Low Readiness Score",
    priority: 10,
  });

  // Rule 2: Poor Sleep (simplified for PoC)
  ruleRegistry.register({
    action: {
      insightTemplate: {
        category: "health",
        description:
          "Your sleep quality is below optimal ({{sleep_score}}). Consider improving your sleep hygiene and recovery.",
        priority: "medium",
        recommendations: [
          "Prioritize sleep hygiene and recovery",
          "Consider reducing workload to allow better rest",
          "Monitor for signs of burnout",
        ],
        title: "Sleep Quality Below Optimal",
      },
      type: "generateInsight",
    },
    condition: {
      operands: [
        {
          aggregation: "latest",
          indicatorId: "oura_sleep_score",
          operator: "<",
          threshold: 70,
        },
      ],
      operator: "AND",
    },
    description: "Detects when sleep quality is below optimal",
    enabled: true,
    id: "rule-002",
    metadata: {
      createdAt: Date.now(),
      createdBy: "system",
      tags: ["health", "productivity", "sleep", "burnout"],
    },
    name: "Poor Sleep Quality",
    priority: 8,
  });

  // Rule 3: High Activity (simplified for PoC - celebrate good activity)
  ruleRegistry.register({
    action: {
      insightTemplate: {
        category: "health",
        description: "Your activity level is excellent ({{activity}}). Keep up the good work!",
        priority: "low",
        recommendations: [
          "Maintain your current activity level",
          "Continue with your exercise routine",
        ],
        title: "Great Activity Level!",
      },
      type: "generateInsight",
    },
    condition: {
      operands: [
        {
          aggregation: "latest",
          indicatorId: "oura_activity",
          operator: ">",
          threshold: 85,
        },
      ],
      operator: "AND",
    },
    description: "Detects when activity level is above optimal",
    enabled: true,
    id: "rule-003",
    metadata: {
      createdAt: Date.now(),
      createdBy: "system",
      tags: ["health", "lifestyle", "activity", "weight"],
    },
    name: "High Activity Level",
    priority: 6,
  });

  // Rule 4: High Steps (simplified for PoC - celebrate high step count)
  ruleRegistry.register({
    action: {
      insightTemplate: {
        category: "health",
        description: "You've achieved {{steps}} steps today. Great job staying active!",
        priority: "low",
        recommendations: ["Keep up the great work", "Maintain your activity level"],
        title: "Excellent Step Count!",
      },
      type: "generateInsight",
    },
    condition: {
      operands: [
        {
          aggregation: "latest",
          indicatorId: "oura_steps",
          operator: ">",
          threshold: 15_000,
        },
      ],
      operator: "AND",
    },
    description: "Detects when daily step count is above 15000",
    enabled: true,
    id: "rule-004",
    metadata: {
      createdAt: Date.now(),
      createdBy: "system",
      tags: ["health", "productivity", "sustainability"],
    },
    name: "High Step Count",
    priority: 9,
  });

  // Rule 5: Good Readiness Score (positive insight for good readiness)
  ruleRegistry.register({
    action: {
      insightTemplate: {
        category: "health",
        description:
          "Your readiness score is great ({{readiness}}). You're well-recovered and ready for the day!",
        priority: "medium",
        recommendations: [
          "Take advantage of your high energy levels",
          "Consider tackling important tasks today",
          "Maintain your recovery routine",
        ],
        title: "Excellent Readiness Score!",
      },
      type: "generateInsight",
    },
    condition: {
      operands: [
        {
          aggregation: "latest",
          indicatorId: "oura_readiness",
          operator: ">=",
          threshold: 80,
        },
      ],
      operator: "AND",
    },
    description: "Detects when readiness score is above optimal",
    enabled: true,
    id: "rule-005",
    metadata: {
      createdAt: Date.now(),
      createdBy: "system",
      tags: ["health", "productivity", "recovery"],
    },
    name: "Good Readiness Score",
    priority: 7,
  });

  // Rule 6: Good Sleep Quality (positive insight for good sleep)
  ruleRegistry.register({
    action: {
      insightTemplate: {
        category: "health",
        description:
          "Your sleep quality is excellent ({{sleep_score}}). Well-rested and ready to perform!",
        priority: "medium",
        recommendations: [
          "Maintain your sleep routine",
          "Your recovery is on track",
          "Continue prioritizing quality sleep",
        ],
        title: "Great Sleep Quality!",
      },
      type: "generateInsight",
    },
    condition: {
      operands: [
        {
          aggregation: "latest",
          indicatorId: "oura_sleep_score",
          operator: ">=",
          threshold: 85,
        },
      ],
      operator: "AND",
    },
    description: "Detects when sleep quality is above optimal",
    enabled: true,
    id: "rule-006",
    metadata: {
      createdAt: Date.now(),
      createdBy: "system",
      tags: ["health", "sleep", "recovery"],
    },
    name: "Good Sleep Quality",
    priority: 7,
  });

  // Rule 7: Moderate Step Count (positive insight for moderate activity)
  ruleRegistry.register({
    action: {
      insightTemplate: {
        category: "health",
        description: "You've achieved {{steps}} steps today. Maintaining a healthy activity level!",
        priority: "low",
        recommendations: ["Keep up the consistent activity", "You're meeting daily movement goals"],
        title: "Good Step Count!",
      },
      type: "generateInsight",
    },
    condition: {
      operands: [
        {
          aggregation: "latest",
          indicatorId: "oura_steps",
          operator: "between",
          threshold: [10_000, 15_000],
        },
      ],
      operator: "AND",
    },
    description: "Detects when daily step count is in good range (10000-15000)",
    enabled: true,
    id: "rule-007",
    metadata: {
      createdAt: Date.now(),
      createdBy: "system",
      tags: ["health", "activity", "lifestyle"],
    },
    name: "Moderate Step Count",
    priority: 5,
  });

  // Rule 8: Combined Health Excellence (multiple good metrics)
  ruleRegistry.register({
    action: {
      insightTemplate: {
        category: "health",
        description:
          "All your health metrics are excellent! Readiness: {{readiness}}, Sleep: {{sleep_score}}, Activity: {{activity}}. You're in great shape!",
        priority: "high",
        recommendations: [
          "You're performing at your peak",
          "Consider taking on challenging tasks",
          "Maintain your current routine",
        ],
        title: "Outstanding Health Metrics!",
      },
      type: "generateInsight",
    },
    condition: {
      operands: [
        {
          aggregation: "latest",
          indicatorId: "oura_readiness",
          operator: ">=",
          threshold: 80,
        },
        {
          aggregation: "latest",
          indicatorId: "oura_sleep_score",
          operator: ">=",
          threshold: 85,
        },
        {
          aggregation: "latest",
          indicatorId: "oura_activity",
          operator: ">=",
          threshold: 85,
        },
      ],
      operator: "AND",
    },
    description: "Detects when multiple health metrics are excellent",
    enabled: true,
    id: "rule-008",
    metadata: {
      createdAt: Date.now(),
      createdBy: "system",
      tags: ["health", "excellence", "performance"],
    },
    name: "Health Excellence",
    priority: 10,
  });

  // NOTE: Registry initialization complete; number of rules is available via ruleRegistry.getAll().length
}
